import { CSSObject, ClickAwayListener, Drawer, IconButton, ListItemIcon, ListItemText, Theme, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useMemo } from 'react';

import { MinusSquare, PlusSquare, XSquare } from 'lucide-react';
import { useIntl } from 'react-intl';

import { useLayoutState } from 'components/layout/layout';
import { NavigationGroup, salmonNavigation, shrimpNavigation } from 'components/layout/utils/navigation';
import { useAccountState } from 'contexts/account/account-context';
import { useThemeState } from 'contexts/theme/ThemeContext';
import { useLogout } from 'hooks/use-logout';
import SidebarItems from './sidebar-items';

type DrawerProps = {
  hide: boolean;
};

const drawerWidth = 275;

const openedMixin = (theme: Theme, hide: boolean = false): CSSObject => ({
  width: hide ? 0 : drawerWidth,
  borderRadius: 0,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: 500,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme, hide: boolean = false): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: 500,
  }),
  borderRadius: 0,
  overflowX: 'hidden',
  width: hide ? 0 : `calc(${theme.spacing(10)})`,
  [theme.breakpoints.up('sm')]: {
    width: hide ? 0 : `calc(${theme.spacing(20)})`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'hide',
})<DrawerProps>(({ theme, open, hide }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme, hide),
    '& .MuiDrawer-paper': openedMixin(theme, hide),
  }),
  ...(!open && {
    ...closedMixin(theme, hide),
    '& .MuiDrawer-paper': closedMixin(theme, hide),
  }),
}));

const Sidebar: React.FC<DrawerProps> = ({ hide }) => {
  const intl = useIntl();
  const theme = useTheme();
  const { aquaSpecies } = useThemeState();
  const { sidebarOpen, handleSidebarToggle } = useLayoutState();
  const logout = useLogout();

  const {
    account: { currentUser },
  } = useAccountState();

  const handleLogout = useCallback(() => {
    void logout();
  }, [logout]);

  const isOverview = location.pathname.split('/')[1] === 'overview';
  const isDeliveryBoard = location.pathname.split('/')[1] === 'delivery-board';

  const navigation = useMemo<NavigationGroup[]>(
    () =>
      aquaSpecies === 'salmon'
        ? salmonNavigation(currentUser?.id, handleLogout)
        : shrimpNavigation(currentUser?.id, handleLogout),
    [aquaSpecies, currentUser?.id, handleLogout]
  );

  return (
    <ClickAwayListener mouseEvent="onMouseUp" touchEvent="onTouchStart" onClickAway={() => {}}>
      <StyledDrawer
        hide={hide}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: theme.palette.darkBlue.main,
            },
          },
        }}
        variant="permanent"
        open={sidebarOpen}>
        <SidebarItems sidebarOpen={sidebarOpen} navigation={navigation} />
        <DrawerHeader
          sx={{
            justifyContent: !sidebarOpen ? 'center' : 'flex-end',
          }}>
          <IconButton onClick={handleSidebarToggle}>
            <ListItemText
              sx={{
                color: 'white',
                mr: sidebarOpen ? 3 : 'auto',
                opacity: sidebarOpen ? 1 : 0,
                display: sidebarOpen ? 'show' : 'none',
              }}
              primary={
                !isOverview || !isDeliveryBoard
                  ? intl.formatMessage({ id: 'general.minimize' })
                  : intl.formatMessage({ id: 'general.close' })
              }
            />
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: sidebarOpen ? 3 : 'auto',
                justifyContent: 'center',
              }}>
              {isOverview || isDeliveryBoard ? (
                <XSquare color={theme.palette.common.white} />
              ) : sidebarOpen ? (
                <MinusSquare size={16} color={theme.palette.common.white} />
              ) : (
                <PlusSquare size={16} color={theme.palette.common.white} />
              )}
            </ListItemIcon>
          </IconButton>
        </DrawerHeader>
      </StyledDrawer>
    </ClickAwayListener>
  );
};

export default Sidebar;
